import types from "../types";
import { ALERTS_ASYNC_STATUS } from "../utils/constants";
import { CREATIVE_ALERT_TYPE } from "../utils/creativeJobAlertConfig";
import {
  CREATIVE_JOB_RTDB_SOURCE,
  overlayCreativeGenerationData,
} from "../utils/creativeJobsNotifications";
import {
  applyGlobalNotificationPending,
  isGlobalNotification,
} from "../utils/globalNotifications";

const initialState = {
  integrationNotFound: {
    open: false,
    items: [],
  },
  pendingIntegrationFormSync: null,
  notifications: [],
  creativeJobsHydrated: false,
  globalAlerts: [],
  globalNotificationSnapshot: [],
  pendingGlobalReadIds: [],
  pendingGlobalDismissedIds: [],
  previousNotifications: [],
  notificationsPanelIsExpanded: false,
  notificationsPanelIsClosed: false,
};

export const alertsReducer = (state = initialState, action) => {
  let itemsFiltered;
  switch (action.type) {
    case types.UI_SET_NOTIFICATIONS_PREVIOUS_NOTIFICATIONS:
      return {
        ...state,
        previousNotifications: action.payload,
      };
    case types.UI_SET_NOTIFICATIONS_PANEL_IS_EXPANDED:
      return {
        ...state,
        notificationsPanelIsExpanded: action.payload,
      };
    case types.UI_SET_NOTIFICATIONS_PANEL_IS_CLOSED:
      return {
        ...state,
        notificationsPanelIsClosed: action.payload,
      };
    case types.ALERTS_SET_INTEGRATIONS_NOT_FOUND: {
      // Skip if the modal is already active to avoid concurrent payloads overwriting.
      if (
        state.integrationNotFound.open ||
        state.integrationNotFound.items.length > 0
      ) {
        return state;
      }
      return {
        ...state,
        integrationNotFound: {
          ...state.integrationNotFound,
          items: Array.isArray(action.payload) ? action.payload : [],
        },
      };
    }
    case types.ALERTS_SHOW_MODAL_INTEGRATION_NOT_FOUND: {
      return {
        ...state,
        integrationNotFound: {
          ...state.integrationNotFound,
          open: action.payload === true,
        },
      };
    }
    case types.ALERTS_RESET_INTEGRATION_NOT_FOUND: {
      return {
        ...state,
        integrationNotFound: {
          open: false,
          items: [],
        },
      };
    }
    case types.ALERTS_SET_PENDING_INTEGRATION_FORM_SYNC: {
      const payload = action.payload;
      const incoming = Array.isArray(payload?.items) ? payload.items : [];
      const prev = state.pendingIntegrationFormSync?.items || [];
      const merged = [...prev, ...incoming];
      return {
        ...state,
        pendingIntegrationFormSync:
          merged.length > 0 ? { items: merged } : null,
      };
    }
    case types.ALERTS_CONSUME_PENDING_INTEGRATION_FORM_SYNC_FOR_CAMPAIGN: {
      const campaignID = action.payload;
      const current = state.pendingIntegrationFormSync;
      if (!current?.items?.length || !campaignID) {
        return state;
      }
      const remaining = current.items.filter(
        (item) => item.campaignID !== campaignID
      );
      return {
        ...state,
        pendingIntegrationFormSync:
          remaining.length > 0 ? { items: remaining } : null,
      };
    }
    case types.GET_FACEBOOK_INTEGRATION:
    case types.GET_GOOGLE_ADS_INTEGRATION:
    case types.GET_TIKTOK_BUSINESS_INTEGRATION:
    case types.GET_GOOGLE_DRIVE_INTEGRATION:
    case types.GET_TIKTOK_INTEGRATION:
    case types.GET_CANVA_INTEGRATION:
    case types.GET_YOUTUBE_INTEGRATION: {
      const integrationID =
        action?.payload?.integrationID || action?.payload?.id;
      itemsFiltered = (state.integrationNotFound.items || []).filter(
        (item) => item.integrationID !== integrationID
      );
      return {
        ...state,
        integrationNotFound: {
          ...state.integrationNotFound,
          items: itemsFiltered,
          open: state.integrationNotFound.open && itemsFiltered.length > 0,
        },
      };
    }

    // New notification cases (migrated from adsCampaignsReducer)
    case types.ALERTS_ADD_NOTIFICATION: {
      // Check if a notification with the same id already exists
      const existingNotificationIndex = state.notifications.findIndex(
        (notification) => notification.id === action.payload.id
      );

      if (existingNotificationIndex !== -1) {
        // If it exists, update the existing notification
        const updatedNotifications = [...state.notifications];
        updatedNotifications[existingNotificationIndex] = {
          ...updatedNotifications[existingNotificationIndex],
          ...action.payload,
          timestamp: Date.now(),
        };
        return {
          ...state,
          notifications: updatedNotifications,
        };
      } else {
        // If it doesn't exist, add new notification
        return {
          ...state,
          notifications: [
            ...state.notifications,
            {
              ...action.payload,
              timestamp: action.payload.timestamp ?? Date.now(),
            },
          ],
        };
      }
    }

    case types.ALERTS_UPDATE_NOTIFICATION: {
      const { id, updates } = action.payload;

      return {
        ...state,
        notifications: state.notifications.map((notification) =>
          notification.id === id
            ? { ...notification, ...updates }
            : notification
        ),
      };
    }

    case types.ALERTS_REMOVE_NOTIFICATION: {
      return {
        ...state,
        notifications: state.notifications.filter(
          (notification) => notification.id !== action.payload.id
        ),
      };
    }

    case types.ALERTS_CLEAR_ALL_NOTIFICATIONS: {
      return {
        ...state,
        notifications: [],
        creativeJobsHydrated: false,
      };
    }

    case types.ALERTS_SYNC_CREATIVE_JOB_NOTIFICATIONS: {
      const creativeJobNotifications = Array.isArray(action.payload)
        ? action.payload
        : [];
      const existingById = new Map(
        state.notifications.map((notification) => [
          notification.id,
          notification,
        ])
      );
      const syncedCreativeJobIds = new Set(
        creativeJobNotifications.map((notification) => notification.id)
      );
      const inProgressSyncedCampaignIds = new Set(
        creativeJobNotifications
          .filter(
            (notification) =>
              notification.type === CREATIVE_ALERT_TYPE.campaign &&
              notification.campaignID &&
              (notification.status === ALERTS_ASYNC_STATUS.LOADING ||
                notification.isGenerating)
          )
          .map((notification) => notification.campaignID)
      );
      const findExistingCampaignNotification = (notification) => {
        const existingByNotificationId = existingById.get(notification.id);
        if (existingByNotificationId) {
          return existingByNotificationId;
        }

        if (
          notification.type !== CREATIVE_ALERT_TYPE.campaign ||
          !notification.campaignID
        ) {
          return undefined;
        }

        return state.notifications.find(
          (item) =>
            item.type === CREATIVE_ALERT_TYPE.campaign &&
            item.id === notification.campaignID &&
            item.campaignID === notification.campaignID &&
            item.source !== CREATIVE_JOB_RTDB_SOURCE
        );
      };
      const nonCreativeJobNotifications = state.notifications.filter(
        (notification) => {
          if (notification.source === CREATIVE_JOB_RTDB_SOURCE) {
            return false;
          }

          if (
            notification.type === CREATIVE_ALERT_TYPE.campaign &&
            syncedCreativeJobIds.has(notification.id)
          ) {
            return false;
          }

          if (
            notification.type === CREATIVE_ALERT_TYPE.campaign &&
            notification.id === notification.campaignID &&
            inProgressSyncedCampaignIds.has(notification.campaignID)
          ) {
            return false;
          }

          return true;
        }
      );
      const orphanedCreativeNotifications = state.notifications.filter(
        (notification) =>
          notification.source === CREATIVE_JOB_RTDB_SOURCE &&
          !syncedCreativeJobIds.has(notification.id) &&
          notification.status === ALERTS_ASYNC_STATUS.LOADING
      );

      const mergedCreativeNotifications = creativeJobNotifications.map(
        (notification) => {
          const existing = findExistingCampaignNotification(notification);

          if (!existing) {
            return {
              ...notification,
              timestamp: notification.timestamp ?? Date.now(),
            };
          }

          if (
            notification.type === CREATIVE_ALERT_TYPE.campaign &&
            existing.type === CREATIVE_ALERT_TYPE.campaign
          ) {
            const mergedStatus = notification.status ?? existing.status;
            const isErrorStatus = mergedStatus === ALERTS_ASYNC_STATUS.ERROR;

            return {
              ...existing,
              ...notification,
              currentCampaign:
                existing.currentCampaign ?? notification.currentCampaign,
              platformGeneratingData:
                existing.platformGeneratingData ??
                notification.platformGeneratingData,
              newPlatforms: existing.newPlatforms ?? notification.newPlatforms,
              buttonAction: existing.buttonAction ?? notification.buttonAction,
              message: isErrorStatus
                ? (notification.message ?? existing.message)
                : (notification.message ?? null),
              ...(isErrorStatus
                ? {}
                : { failedCampaigns: [], errorsCampaigns: {} }),
              timestamp:
                existing.timestamp ?? notification.timestamp ?? Date.now(),
            };
          }

          return {
            ...notification,
            generationData: overlayCreativeGenerationData(
              existing.generationData,
              notification.generationData
            ),
            timestamp:
              existing?.timestamp ?? notification.timestamp ?? Date.now(),
          };
        }
      );

      return {
        ...state,
        creativeJobsHydrated: true,
        notifications: [
          ...nonCreativeJobNotifications,
          ...mergedCreativeNotifications,
          ...orphanedCreativeNotifications,
        ],
      };
    }

    case types.ALERTS_PROMOTE_CREATIVE_NOTIFICATION: {
      const { fromId, jobID } = action.payload || {};
      if (!fromId || !jobID || fromId === jobID) {
        return state;
      }

      const hasSourceAlert = state.notifications.some(
        (notification) => notification.id === fromId
      );
      if (!hasSourceAlert) {
        return state;
      }

      const notificationsWithoutDuplicateJob = state.notifications.filter(
        (notification) =>
          notification.id !== jobID || notification.id === fromId
      );

      return {
        ...state,
        notifications: notificationsWithoutDuplicateJob.map((notification) =>
          notification.id === fromId
            ? {
                ...notification,
                id: jobID,
                jobID,
                source: CREATIVE_JOB_RTDB_SOURCE,
              }
            : notification
        ),
      };
    }

    case types.ALERTS_SYNC_GLOBAL_NOTIFICATIONS: {
      const payload = action.payload || {};
      const snapshot = Array.isArray(payload.alerts) ? payload.alerts : [];
      const serverReadIds = new Set(payload.readIds || []);
      const serverDismissedIds = new Set(payload.dismissedIds || []);
      const pendingGlobalReadIds = state.pendingGlobalReadIds.filter(
        (id) => !serverReadIds.has(id)
      );
      const pendingGlobalDismissedIds = state.pendingGlobalDismissedIds.filter(
        (id) => !serverDismissedIds.has(id)
      );

      return {
        ...state,
        globalNotificationSnapshot: snapshot,
        pendingGlobalReadIds,
        pendingGlobalDismissedIds,
        globalAlerts: applyGlobalNotificationPending(
          snapshot,
          pendingGlobalReadIds,
          pendingGlobalDismissedIds
        ),
        notifications: state.notifications.filter(
          (notification) => !isGlobalNotification(notification)
        ),
      };
    }

    case types.ALERTS_PENDING_READ_GLOBAL_NOTIFICATIONS: {
      const ids = Array.isArray(action.payload) ? action.payload : [];
      const pendingGlobalReadIds = [
        ...new Set([...state.pendingGlobalReadIds, ...ids]),
      ];

      return {
        ...state,
        pendingGlobalReadIds,
        globalAlerts: applyGlobalNotificationPending(
          state.globalNotificationSnapshot,
          pendingGlobalReadIds,
          state.pendingGlobalDismissedIds
        ),
      };
    }

    case types.ALERTS_CLEAR_PENDING_READ_GLOBAL_NOTIFICATIONS: {
      const ids = new Set(Array.isArray(action.payload) ? action.payload : []);
      const pendingGlobalReadIds = state.pendingGlobalReadIds.filter(
        (id) => !ids.has(id)
      );

      return {
        ...state,
        pendingGlobalReadIds,
        globalAlerts: applyGlobalNotificationPending(
          state.globalNotificationSnapshot,
          pendingGlobalReadIds,
          state.pendingGlobalDismissedIds
        ),
      };
    }

    case types.ALERTS_PENDING_DISMISS_GLOBAL_NOTIFICATION: {
      const notificationId = action.payload;
      if (!notificationId) {
        return state;
      }
      const pendingGlobalDismissedIds = [
        ...new Set([...state.pendingGlobalDismissedIds, notificationId]),
      ];

      return {
        ...state,
        pendingGlobalDismissedIds,
        globalAlerts: applyGlobalNotificationPending(
          state.globalNotificationSnapshot,
          state.pendingGlobalReadIds,
          pendingGlobalDismissedIds
        ),
      };
    }

    case types.ALERTS_CLEAR_PENDING_DISMISS_GLOBAL_NOTIFICATION: {
      const pendingGlobalDismissedIds = state.pendingGlobalDismissedIds.filter(
        (id) => id !== action.payload
      );

      return {
        ...state,
        pendingGlobalDismissedIds,
        globalAlerts: applyGlobalNotificationPending(
          state.globalNotificationSnapshot,
          state.pendingGlobalReadIds,
          pendingGlobalDismissedIds
        ),
      };
    }

    case types.GLOBAL_RESET_STATE:
      return initialState;
    case types.GLOBAL_RESET_ADVERTISER_DATA: {
      return {
        ...initialState,
        globalAlerts: state.globalAlerts,
        globalNotificationSnapshot: state.globalNotificationSnapshot,
        pendingGlobalReadIds: state.pendingGlobalReadIds,
        pendingGlobalDismissedIds: state.pendingGlobalDismissedIds,
      };
    }

    default:
      return state;
  }
};
